import { ChatAppearanceButton } from './ChatAppearanceButton';
import { useIsNarrowViewport } from '@/lib/useIsNarrowViewport';
import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover } from '@heroui/react';
import { Tooltip } from '@/components/Tooltip';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { ConnectionStatus } from '@/components/ui/ConnectionStatus';
import { getUiPrefs, subscribeUiPrefs, updateUiPrefs } from '@/lib/uiPrefs';
import { TeamTag } from '@/components/TeamTag';
import { MoreHorizontalIcon, HashIcon, PhoneIcon, SettingsIcon, UsersIcon, VolumeIcon } from '@/components/icons';
import { isSelfVault } from '@/components/channelDrawer/primitives';
import { startOutgoingCall } from '@/lib/dmCall';
import { emitAppEvent } from '@/lib/appEvents';
import { PinnedBar, PinnedMessagesButton } from '@/components/channelInfoBar/PinnedPanel';
import { SearchButton } from '@/components/channelInfoBar/SearchButton';
import { MuteButton } from '@/components/channelInfoBar/MuteButton';
import { VaultPanel } from '@/components/vault/VaultPanel';
import { HeroAvatarGroup } from '@/heroui/HeroAvatarGroup';
import { UserCardTrigger } from '@/components/userCard/UserCardTrigger';

const GroupDmSettingsModal = lazyWithReload(() =>
  import('@/components/GroupDmSettingsModal').then((m) => ({ default: m.GroupDmSettingsModal })),
);

/**
 * Channel header for the HeroUI shell. From-scratch HeroUI surface + a
 * type icon, server/conversation breadcrumb and topic, plus the action
 * rail (call / mute / pinned / search / members toggle). Reuses the
 * popover-carrying action buttons (Mute/Pinned/Search) for their logic
 * while presentation follows the HeroUI bar; replaces the Aether
 * ChannelInfoBar inside HeroApp.
 */
export function HeroChannelHeader({
  focusMode = false,
  onToggleFocus,
}: {
  focusMode?: boolean;
  onToggleFocus?: () => void;
} = {}) {
  const { t } = useTranslation();
  const narrow = useIsNarrowViewport();
  const channelId = useDataStore((s) => s.currentChannelId);
  const guildId = useDataStore((s) => s.currentGuildId);
  const guild = useDataStore((s) => (guildId ? s.guilds[guildId] : undefined));
  const dmChannel = useDataStore((s) => (channelId ? s.dmChannels[channelId] : undefined));
  const me = useAuthStore((s) => s.user);
  const channel = guild?.channels.find((c) => c.id === channelId) ?? dmChannel ?? null;
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);

  if (!channel) return null;

  const directPeer = dmChannel?.directParticipants.find((p) => p.userId !== me?.id);
  const isGroupDm = dmChannel?.type === 'GROUP_DM';
  // In a 1:1 DM the header identity (avatar + name) opens the shared UserCard —
  // the same card as message authors / member rows. «Написать» is hidden there
  // since we're already in that DM.
  const dmPeerCard =
    dmChannel?.type === 'DM' && directPeer
      ? {
          id: directPeer.userId,
          username: directPeer.user.username,
          displayName: directPeer.user.displayName ?? null,
          avatarUrl: directPeer.user.avatarUrl ?? null,
        }
      : null;
  // Personal "Сейф" (self-chat): a DM whose only participant is you. It has no
  // "other peer", so without this it fell back to channel.name → "(unnamed)".
  const selfVault = !!dmChannel && isSelfVault(dmChannel, me?.id ?? null);
  const label = selfVault
    ? t('ui.saved.title')
    : (isGroupDm
        ? dmChannel.name ||
          dmChannel.directParticipants
            .filter((p) => p.userId !== me?.id)
            .slice(0, 3)
            .map((p) => p.user.displayName || p.user.username)
            .join(', ')
        : directPeer?.user.displayName || directPeer?.user.username) ||
      channel.name ||
      '(unnamed)';
  const Icon = channel.type === 'VOICE' ? VolumeIcon : isGroupDm ? UsersIcon : HashIcon;

  return (
    <>
      {groupSettingsOpen && channelId && (
        <Suspense fallback={null}>
          <GroupDmSettingsModal channelId={channelId} onClose={() => setGroupSettingsOpen(false)} />
        </Suspense>
      )}
      <div className={`studio-heading flex shrink-0 items-center gap-3 border-b border-separator px-4 transition-[height] ${focusMode ? 'h-12' : 'h-16'}`}>
        {/* Mobile-only left button (md:hidden):
            - in a server channel → hamburger that opens the channel drawer
              (classic flow, unchanged);
            - in DMs → a back arrow that returns to the DM list (deselects). */}
        {guildId ? (
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label={t('header.channelMenu')}
            onPress={() => emitAppEvent('mobile:toggle-drawer')}
            className="-ml-1 shrink-0 md:hidden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="size-5" aria-hidden>
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </Button>
        ) : (
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label={t('header.backToList')}
            onPress={() => useDataStore.getState().selectChannel(null)}
            className="-ml-1 shrink-0 md:hidden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5" aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Button>
        )}
        <div className="studio-heading-identity flex min-w-0 flex-1 items-center gap-2.5">
          {dmPeerCard && directPeer ? (
            // 1:1 DM — avatar + name are one click target that opens the UserCard.
            <UserCardTrigger
              user={dmPeerCard}
              guildId={null}
              hideMessage
              label={t('header.openProfile')}
              className="-mx-1 flex min-w-0 items-center gap-2.5 rounded-md px-1 transition-colors hover:bg-white/5"
            >
              <IdentityAvatar id={directPeer.userId} name={label} src={directPeer.user.avatarUrl} size="md" />
              <span className="truncate text-base font-semibold text-foreground">{label}</span>
            </UserCardTrigger>
          ) : null}
          {dmPeerCard && directPeer && directPeer.user.teamTag ? (
            <TeamTag tag={directPeer.user.teamTag} className="studio-heading-team -ml-1" />
          ) : null}
          {dmPeerCard && directPeer ? null : isGroupDm && dmChannel ? (
            // Group DM — the avatar cluster + name open the group settings modal
            // (members / settings), same as the gear on the right.
            <button
              type="button"
              onClick={() => setGroupSettingsOpen(true)}
              aria-label={t('header.groupSettings')}
              className="-mx-1 flex min-w-0 items-center gap-2.5 rounded-md px-1 text-left transition-colors hover:bg-foreground/5"
            >
              {narrow ? <UsersIcon className="size-5 shrink-0 text-muted" /> : (
                <HeroAvatarGroup
                  users={dmChannel.directParticipants.map((p) => ({
                    id: p.userId,
                    name: p.user.displayName || p.user.username,
                    avatarUrl: p.user.avatarUrl,
                  }))}
                  max={3}
                />
              )}
              <span className="studio-heading-title flex min-w-0 items-baseline gap-2">
                <span className="truncate text-base font-semibold text-foreground">{label}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted">
                  · {t('header.members', { count: dmChannel.directParticipants.length })}
                </span>
              </span>
            </button>
          ) : (
            <>
              {!selfVault && (isGroupDm ? (
                <HeroAvatarGroup
                  users={dmChannel.directParticipants.map((p) => ({
                    id: p.userId,
                    name: p.user.displayName || p.user.username,
                    avatarUrl: p.user.avatarUrl,
                  }))}
                  max={3}
                />
              ) : (
                <Icon
                  className={
                    'size-5 shrink-0 ' + (channel.type === 'VOICE' ? 'text-accent' : 'text-muted')
                  }
                />
              ))}
              <span className="studio-heading-title flex min-w-0 items-baseline gap-2">
                {guild?.name && !isGroupDm && (
                  <span className="hidden truncate text-sm text-muted md:inline">
                    {guild.name}
                    <span className="mx-1.5 opacity-50">/</span>
                  </span>
                )}
                <span className="truncate text-base font-semibold text-foreground">{label}</span>
                {isGroupDm && (
                  <span className="shrink-0 text-xs tabular-nums text-muted">
                    · {t('header.members', { count: dmChannel.directParticipants.length })}
                  </span>
                )}
              </span>
            </>
          )}
          {channel.topic && (
            <>
              <span className="h-4 w-px shrink-0 bg-separator" />
              <span className="studio-heading-topic truncate text-sm text-muted">{channel.topic}</span>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {channel.type === 'VOICE' && onToggleFocus && (
            <Tooltip label={focusMode ? t('header.exitFocusHint') : t('header.enterFocusHint')}>
                <Button aria-label={focusMode ? t('header.exitFocus') : t('header.enterFocus')} isIconOnly size="sm" variant={focusMode ? 'primary' : 'ghost'} onPress={onToggleFocus} className="group">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-[18px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]" aria-hidden>
                    {focusMode ? <path d="M8 3v5H3M16 3v5h5M8 21v-5H3M16 21v-5h5" /> : <path d="M3 8V3h5M21 8V3h-5M3 16v5h5M21 16v5h-5" />}
                  </svg>
                </Button>
            </Tooltip>
          )}
          {isGroupDm && !narrow && (
            <Tooltip label={t('header.groupSettings')}>
                <Button aria-label={t('header.groupSettings')} isIconOnly size="sm" variant="ghost" onPress={() => setGroupSettingsOpen(true)} className="group">
                  <SettingsIcon className="size-[18px] transition-transform duration-700 ease-out group-hover:rotate-[360deg]" />
                </Button>
            </Tooltip>
          )}
          {channelId && dmChannel?.type === 'DM' && directPeer && (
            <Tooltip label={t('header.call')}>
                <Button aria-label={t('header.call')}
                  isIconOnly
                  size="sm"
                  variant="ghost"
                  className="group"
                  onPress={() =>
                    startOutgoingCall(channelId, {
                      displayName: directPeer.user.displayName || directPeer.user.username,
                      avatarUrl: directPeer.user.avatarUrl,
                    })
                  }
                >
                  <PhoneIcon className="size-[18px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]" />
                </Button>
            </Tooltip>
          )}
          {channel.type !== 'VOICE' && !narrow && <ChatAppearanceButton />}
          {narrow ? <Popover>
            <Popover.Trigger aria-label={t('messages.moreActions')} className="grid min-h-11 min-w-11 place-items-center rounded-lg text-muted">
              <MoreHorizontalIcon className="size-5" />
            </Popover.Trigger>
            <Popover.Content className="studio-header-popover" placement="bottom end">
              <Popover.Dialog className="studio-header-menu flex items-center gap-2 p-3" aria-label={t('messages.moreActions')}>
          {channel.type !== 'VOICE' && <ChatAppearanceButton />}
          {selfVault && <VaultPanel />}
          {channelId && <MuteButton channelId={channelId} guildId={guildId ?? null} />}
          {channelId && <PinnedMessagesButton channelId={channelId} />}
          {channelId && <SearchButton channelId={channelId} />}
              </Popover.Dialog>
            </Popover.Content>
          </Popover> : <>
          {selfVault && <VaultPanel />}
          {channelId && <MuteButton channelId={channelId} guildId={guildId ?? null} />}
          {channelId && <PinnedMessagesButton channelId={channelId} />}
          {channelId && <SearchButton channelId={channelId} />}
          </>}
          {guildId && <MembersPanelToggle />}
        </div>
      </div>
      {channelId && <PinnedBar channelId={channelId} />}
      <ConnectionStatus />
    </>
  );
}

function MembersPanelToggle() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(getUiPrefs().membersPanelOpen);
  useEffect(() => subscribeUiPrefs(() => setOpen(getUiPrefs().membersPanelOpen)), []);
  // On desktop the panel is inline and its open/closed state is the persisted
  // `membersPanelOpen` pref (button reflects it). On mobile/tablet the panel is
  // an ephemeral drawer toggled via an app event, so we don't touch the pref
  // (and the button stays a neutral ghost like the channel hamburger).
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const active = isDesktop && open;
  return (
    <Tooltip label={active ? t('header.hideMembersShort') : t('header.showMembersShort')}>
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={active ? t('header.hideMembers') : t('header.showMembers')}
          aria-pressed={active}
          onPress={() =>
            isDesktop
              ? updateUiPrefs({ membersPanelOpen: !open })
              : emitAppEvent('mobile:toggle-members')
          }
          className={'group ' + (active ? 'bg-default/60 text-foreground' : 'text-muted')}
        >
          <UsersIcon className="size-[18px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]" />
        </Button>
    </Tooltip>
  );
}
